import React, { useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    DateField, Icon, Loader, Accordion
} from '@jutro/components';
import { TranslatorContext, FormattedMessage } from '@jutro/locale';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import HomeCoverageTable from '../HomeCoverageTable/HomeCoverageTable';
import metadata from './PolicyMarineInfo.metadata.json5';
import messages from './PolicyMarineInfo.messages';
import styles from './PolicyMarineInfo.module.scss';

const getCell = (items, index, property) => {
    if(property.id == "percentageUsage"){
        return !_.isObject(items[property.id]) ? items[property.id] + '%' : null;
    }
        
    return !_.isObject(items[property.id]) ? items[property.id] : null;    
};

function PolicyMarineInfo(props) {
    const translator = useContext(TranslatorContext);
    const [openItems, setOpenItems] = useState([]);
    const updateCoverages = (dwellingCoverages) => {
        const dwellingDTO = _.cloneDeep(dwellingCoverages);
        const coverages = dwellingDTO.coverageDTOs;
        const allCoverages = {
            sectionICoverages: [],
            sectionIICoverages: [],
            additionalLiabilityCoverages: [],
            additionalPropertyCoverages: [],
            scheduledItems: [],
            endorsements: [],
            optionalCoverages: [],
            combinedManOptCoverages: [],
            discounts: [],
            addlInterests: []
        };
        coverages.forEach((coverage) => {
            const cov = coverage;

            switch (cov.categoryCodeIdentifier) {
                case 'HOPSectionICovCat':
                case 'HODW_SectionI_HOE':
                    allCoverages.sectionICoverages.push(cov);
                    allCoverages.combinedManOptCoverages.push(cov);
                    break;
                case 'HOPSectionIICovCat':
                case 'HODW_SectionII_HOE':
                    allCoverages.sectionIICoverages.push(cov);
                    allCoverages.combinedManOptCoverages.push(cov);
                    break;
                case 'HO_Endorsement_EY':
                    allCoverages.endorsements.push(cov);
                    break;
                case 'HOPOptionalCovCat':
                    allCoverages.optionalCoverages.push(cov);
                    allCoverages.combinedManOptCoverages.push(cov);
                    break;
                case 'HOPAdditionalCovCat':
                case 'HOLI_Additional_HOE':
                    allCoverages.additionalLiabilityCoverages.push(cov);
                    break;
                case 'HOPPCAdditionalCovCat':
                case 'HODW_Additional_HOE':
                    allCoverages.additionalPropertyCoverages.push(cov);
                    break;
                case 'HOPScheduledItemCovCat':
                case 'HODW_SchedulesCov_HOE':
                    if (cov.specialLimits) {
                        cov.specialLimits.forEach((coverageItem) => {
                            allCoverages.scheduledItems.push(coverageItem);
                        });
                    } else if (cov.scheduledProperty) {
                        cov.scheduledProperty.forEach((coverageItem) => {
                            allCoverages.scheduledItems.push(coverageItem);
                        });
                    }
                    break;
                default:
                    break;
            }
        });
        allCoverages.discounts = dwellingDTO.discounts;
        allCoverages.addlInterests = dwellingDTO.additionalInterests;
        return allCoverages;
    };

    const getHomeOwnersData = useCallback(() => {
        const homeOwnersData = _.cloneDeep(props.value);
        homeOwnersData.dwellingDTO.forEach((dwellingDTO, index) => {

            homeOwnersData.dwellingDTO[index].allCoverages = updateCoverages(dwellingDTO);
        });


        return homeOwnersData;
    }, [props, translator]);

    const homeOwnersData = getHomeOwnersData();

    if (_.isEmpty(homeOwnersData)) {
        return null;
    }

    //Show/Hide package details code -- Start--
    const toggleShowHide = useCallback(
        (item) => {
            const openedItem = _.clone(openItems);
            const index = _.indexOf(openedItem, item);
            if (index === -1) {
                openedItem.push(item);
                setOpenItems(openedItem);
            } else {
                setOpenItems(_.slice(openedItem, index, 0));
            }
        },
        [openItems]
    );

    const generatePackageDetails = useCallback(() => {
        const homeOwnersData = _.cloneDeep(props.value);

        const overrides = homeOwnersData.dwellingDTO.map((packageDTO, index) => {
            const marine_EY = homeOwnersData.dwellingDTO[index].marine_EY;
            const coverageData = homeOwnersData.dwellingDTO[index].coverageDTOs;
            const isOutBoard = homeOwnersData.dwellingDTO[index].marine_EY.motorType == "Outboard" ? true : false

            return {
                [`packageDetailsDiv${index}`]: {
                    show: openItems
                },
                [`motorDetailsTable${index}`]: {
                    data: marine_EY.marineMotorDetails
                },
                [`showContentToggleLink${index}`]: {
                    onClick: () => toggleShowHide(index)
                },
                [`contentDetailToggle${index}`]: {
                    icon: _.includes(openItems, index) ? 'caret-up' : 'caret-down',
                    message: _.includes(openItems, index)
                        ? translator(messages.hidePackageCoverages)
                        : translator(messages.showPackageCoverages)
                },
                [`packageDetailsID${index}`]: {
                    visible: _.includes(openItems, index)
                },
                [`coveragesTableContent${index}`]: {
                    value: coverageData,
                    isMarineCov: true
                },
                [`operatorDetailsTable${index}`]: {
                    data: marine_EY.operators
                },
                [`attatchedEquipmentTable${index}`]: {
                    data: marine_EY.additionalEquipments,
                    visible: !_.isEmpty(marine_EY.additionalEquipments)
                },
                [`MotorSerial${index}`]: {
                    visible: isOutBoard
                },
                [`motorMake${index}`]: {
                    visible: isOutBoard
                },
                [`motorYearBuilt${index}`]: {
                    visible: isOutBoard
                },
                [`hp${index}`]: {
                    visible: isOutBoard
                },
                [`motorValue${index}`]: {
                    visible: isOutBoard
                },
                [`maxSpeed${index}`]: {
                    visible: !isOutBoard
                }
            };
        });

        return Object.assign({}, ...overrides);
    }, [homeOwnersData, openItems, props.value, toggleShowHide, translator]);
    //Show/Hide package details code -- End--

    const overrides = {
        '@field': {
            readOnly: true
        },
        homeDetailsSeparator: {
            visible: !_.isEmpty(homeOwnersData.mortgageeDetailsDTO)
        },
        ...generatePackageDetails()
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
            homecoveragetablecomponent: HomeCoverageTable
        },
        resolveCallbackMap: {
            getCell
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={homeOwnersData}
            overrideProps={overrides}
            classNameMap={resolvers.resolveClassNameMap}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
        />
    );
}

PolicyMarineInfo.propTypes = {
    value: PropTypes.shape({}).isRequired
};

export default PolicyMarineInfo;
